import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Search, Download, Filter, Columns, CheckSquare, Square, RotateCcw, Copy, Check,
} from 'lucide-react';

import Breadcrumb, { BREADCRUMB_EXPORT_BTN, BREADCRUMB_GHOST_BTN } from '../../../components/Breadcrumb/Breadcrumb';
import Table, { TABLE_THEME } from '../../../components/Table/Table';
import { toIsoDate } from '../../../utils/dateFormat';
import { fetchBankReportDetailsByDate } from '../../../store/slices/payrollSlice';
import { getGradeList } from '../../../store/slices/gradeSlice';

const FIELD = `${TABLE_THEME.fieldBase} ${TABLE_THEME.fieldFocus}`;
const LABEL = TABLE_THEME.fieldLabel;

const ALL_COLUMNS = [
    { key: 'id', label: 'Tran ID', defaultSelected: false },
    { key: 'nicNumber', label: 'NIC Number', defaultSelected: true },
    { key: 'bankName', label: 'Bank Name', defaultSelected: true },
    { key: 'destinationBank', label: 'Destination Bank', defaultSelected: false },
    { key: 'branchName', label: 'Branch Name', defaultSelected: false },
    { key: 'destinationBr', label: 'Destination Br', defaultSelected: false },
    { key: 'branchSwiftCode', label: 'Branch Swift Code', defaultSelected: false },
    { key: 'destinationAccount', label: 'Destination Account', defaultSelected: true },
    { key: 'destinationAccountName', label: 'Destination Account Name', defaultSelected: true },
    { key: 'trnCode', label: 'TRN Code', defaultSelected: false },
    { key: 'returnCode', label: 'Return Code', defaultSelected: false },
    { key: 'crDrCode', label: 'Cr/Dr Code', defaultSelected: false },
    { key: 'returnDate', label: 'Return Date', defaultSelected: false },
    { key: 'amount', label: 'Amount', defaultSelected: true },
    { key: 'amountDecimal', label: 'Amount Decimal', defaultSelected: false },
    { key: 'currencyCode', label: 'Currency Code', defaultSelected: false },
    { key: 'originatingAccount', label: 'Originating Account', defaultSelected: false },
    { key: 'originatingAccountName', label: 'Originating Account Name', defaultSelected: false },
    { key: 'particulars', label: 'Particulars', defaultSelected: false },
    { key: 'reference', label: 'Reference', defaultSelected: false },
    { key: 'valueDate', label: 'Value Date', defaultSelected: false },
    { key: 'securityField', label: 'Security Field', defaultSelected: false },
    { key: 'filler', label: 'Filler', defaultSelected: false },
];

const PRESETS = {
    essential: ['id', 'bankName', 'destinationAccount', 'destinationAccountName', 'amount', 'nicNumber'],
    bankTransfer: ['bankName', 'destinationBank', 'branchName', 'destinationAccount', 'destinationAccountName', 'amount', 'currencyCode', 'reference'],
    all: ALL_COLUMNS.map((c) => c.key),
};

/* How each field should read once it lands in a cell. */
const MONEY_KEYS = new Set(['amount', 'amountDecimal']);
const DATE_KEYS = new Set(['returnDate']);
const CENTRED_KEYS = new Set([
    'id', 'destinationBr', 'trnCode', 'returnCode', 'crDrCode',
    'currencyCode', 'valueDate', 'securityField', 'filler',
]);
const NUMERIC_KEYS = new Set([
    'id', 'destinationBank', 'destinationBr', 'destinationAccount', 'trnCode', 'returnCode',
    'crDrCode', 'originatingAccount', 'valueDate', 'securityField', 'nicNumber',
]);
/* The first of these that is on screen soaks up the leftover width. */
const GROW_PREFERENCE = ['destinationAccountName', 'bankName', 'originatingAccountName', 'particulars', 'branchName', 'reference'];

const fmtAmount = (v) => {
    const num = parseFloat(v);
    return Number.isNaN(num)
        ? ''
        : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function PayrollTextCustom() {
    const dispatch = useDispatch();

    const { items = [], loading = false, error = null } = useSelector(
        (state) => state.payroll?.bankReport || {}
    );

    const grades = useSelector((state) => state.gradeSlice?.grades || []);
    const [selectedGrade, setSelectedGrade] = useState('');

    const [search, setSearch] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [copied, setCopied] = useState(false);
    const [exportError, setExportError] = useState('');

    const [selectedColumnKeys, setSelectedColumnKeys] = useState(
        ALL_COLUMNS.filter((c) => c.defaultSelected).map((c) => c.key)
    );

    const [columnSearch, setColumnSearch] = useState('');
    const [showColumnPanel, setShowColumnPanel] = useState(true);

    const hasData = items && items.length > 0;

    useEffect(() => {
        dispatch(getGradeList());
    }, [dispatch])

    useEffect(() => {
        const now = new Date();
        const defaultDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        setSelectedDate(defaultDate);
    }, []);

    useEffect(() => {
        if (selectedDate) {
            dispatch(fetchBankReportDetailsByDate({ date: selectedDate, gradeCode: selectedGrade }));
        }
    }, [selectedDate, selectedGrade, dispatch]);

    /* Search stays on the page rather than inside Table, because Export and
       Copy have to write out exactly the rows the search is showing. */
    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((row) =>
            Object.values(row).some((val) => String(val || '').toLowerCase().includes(q))
        );
    }, [items, search]);

    const activeColumns = useMemo(
        () => ALL_COLUMNS.filter((col) => selectedColumnKeys.includes(col.key)),
        [selectedColumnKeys]
    );

    const filteredColumnDefs = useMemo(() => {
        const q = columnSearch.trim().toLowerCase();
        if (!q) return ALL_COLUMNS;
        return ALL_COLUMNS.filter((col) => col.label.toLowerCase().includes(q));
    }, [columnSearch]);

    /* Turn the picked fields into Table column defs. */
    const tableColumns = useMemo(() => {
        const growKey = GROW_PREFERENCE.find((k) => selectedColumnKeys.includes(k));

        return activeColumns.map((col) => {
            const money = MONEY_KEYS.has(col.key);
            const date = DATE_KEYS.has(col.key);
            const centred = CENTRED_KEYS.has(col.key);
            const numeric = NUMERIC_KEYS.has(col.key);

            return {
                key: col.key,
                label: col.label,
                align: money ? 'right' : centred || date ? 'center' : 'left',
                grow: col.key === growKey,
                className: money
                    ? 'font-semibold text-gray-900'
                    : col.key === 'bankName'
                    ? 'font-semibold text-gray-900'
                    : `text-gray-600${numeric || date ? ' tabular-nums' : ''}`,
                render: money
                    // ? (r) => fmtAmount(r[col.key])
                    ? (r) => fmtAmount(r.NET_SALARY ?? r.netSalary ?? r[col.key])
                    : date
                    ? (r) => toIsoDate(r[col.key])
                    : undefined,
            };
        });
    }, [activeColumns, selectedColumnKeys]);

    const toggleColumn = (key) => {
        setSelectedColumnKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const applyPreset = (presetName) => {
        if (PRESETS[presetName]) {
            setSelectedColumnKeys(PRESETS[presetName]);
        }
    };

    const selectAllColumns = () => {
        setSelectedColumnKeys(ALL_COLUMNS.map((c) => c.key));
    };

    const deselectAllColumns = () => {
        setSelectedColumnKeys([]);
    };

    const handleExportCSV = () => {
        try {
            setExportError('');
            if (filteredRows.length === 0) {
                setExportError('No data to export.');
                return;
            }

            if (activeColumns.length === 0) {
                setExportError('Please select at least one column to export.');
                return;
            }

            const headers = activeColumns.map((c) => c.label);
            const csvRows = [headers.join(',')];

            for (const row of filteredRows) {
                const values = activeColumns.map((col) => {
                    // let val = row[col.key] || '';

                    let val = MONEY_KEYS.has(col.key) 
                        ? (row.NET_SALARY ?? row.netSalary ?? row[col.key])
                        : row[col.key];
                        
                    val = val || '';

                    if (typeof val === 'string') {
                        val = `"${val.replace(/"/g, '""')}"`;
                    }
                    return val;
                });
                csvRows.push(values.join(','));
            }

            const csvString = csvRows.join('\n');
            const blob = new Blob(['﻿' + csvString], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);

            // Determine grade name for the file
            const gradeName = selectedGrade 
                ? grades.find(g => g.HGD_GRADE_CODE === selectedGrade)?.HGD_GRADE_NAME || selectedGrade
                : 'All Grades';
            const safeFileNameGrade = gradeName.replace(/[^a-z0-9]/gi, '_');

            link.setAttribute(
                'download',
                `Custom_Payroll_Text_${selectedDate}_${safeFileNameGrade}_${new Date().toISOString().split('T')[0]}.csv`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export CSV:', err);
            setExportError('Failed to export data.');
        }
    };

    const handleCopyText = () => {
        if (filteredRows.length === 0 || activeColumns.length === 0) return;

        const headers = activeColumns.map((c) => c.label).join('\t');
        const rows = filteredRows.map((row) =>
            // activeColumns.map((col) => row[col.key] || '').join('\t')

            activeColumns.map((col) => {
                let val = MONEY_KEYS.has(col.key)
                    ? (row.NET_SALARY ?? row.netSalary ?? row[col.key])
                    : row[col.key];

                return val || '';
            }).join('\t')
        );

        const fullText = [headers, ...rows].join('\n');

        /* navigator.clipboard is undefined on insecure origins, so fall back to
           a hidden textarea instead of throwing and leaving the button dead. */
        const copyToClipboard = (text) => {
            if (navigator.clipboard?.writeText) {
                return navigator.clipboard.writeText(text);
            }
            const area = document.createElement('textarea');
            area.value = text;
            area.style.position = 'fixed';
            area.style.opacity = '0';
            document.body.appendChild(area);
            area.select();
            try {
                document.execCommand('copy');
            } finally {
                document.body.removeChild(area);
            }
            return Promise.resolve();
        };

        Promise.resolve(copyToClipboard(fullText))
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch((err) => {
                console.error('Failed to copy:', err);
                setExportError('Could not copy to the clipboard.');
            });
    };

    const displayError = error || exportError;
    const actionsDisabled = !hasData || filteredRows.length === 0 || activeColumns.length === 0;

    return (
        <div className="p-6 space-y-4">
            {/* ── Header ─────────────────────────────────────────────── */}
            <Breadcrumb
                trail={['Payroll', 'Custom Payroll Text']}
                title="Custom Payroll Text"
                subtitle="Pick the Bank Report columns you need, preview the table, then export or copy it."
                action={
                    <>
                        <button
                            onClick={() => setShowColumnPanel(!showColumnPanel)}
                            className={
                                showColumnPanel
                                    ? 'flex items-center gap-2 h-9 px-3 rounded-lg text-xs font-semibold transition-colors bg-primary-50 text-primary-700 border border-primary-200'
                                    : BREADCRUMB_GHOST_BTN
                            }
                        >
                            <Filter size={14} />
                            Columns ({selectedColumnKeys.length}/{ALL_COLUMNS.length})
                        </button>

                        <button
                            onClick={handleCopyText}
                            disabled={actionsDisabled}
                            title="Copy table contents (TSV format)"
                            className={BREADCRUMB_GHOST_BTN}
                        >
                            {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                            {copied ? 'Copied!' : 'Copy TSV'}
                        </button>

                        <button
                            onClick={handleExportCSV}
                            disabled={actionsDisabled}
                            className={BREADCRUMB_EXPORT_BTN}
                        >
                            <Download size={15} />
                            Export CSV
                        </button>
                    </>
                }
            />

            {displayError && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
                    {displayError}
                    <span className="block text-xs text-amber-700 mt-0.5">Try choosing another month.</span>
                </div>
            )}

            {/* ── Column picker + table ──────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                {showColumnPanel && (
                    <div className="md:col-span-4 lg:col-span-3 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                Select Columns
                                <span className="bg-primary-50 text-primary-700 text-[11px] px-1.5 py-0.5 rounded-full font-semibold">
                                    {selectedColumnKeys.length}
                                </span>
                            </h3>
                            <button
                                onClick={deselectAllColumns}
                                title="Clear all selections"
                                className="text-[11px] text-gray-500 hover:text-red-600 flex items-center gap-1 transition-colors"
                            >
                                <RotateCcw size={11} /> Clear
                            </button>
                        </div>

                        <div className="p-4 space-y-3">
                            {/* Presets */}
                            <div>
                                <span className={LABEL}>Presets</span>
                                <div className="flex flex-wrap gap-1.5">
                                    <button
                                        onClick={() => applyPreset('essential')}
                                        className="px-2 py-1 text-[11px] rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold transition-colors"
                                    >
                                        Essential (6)
                                    </button>
                                    <button
                                        onClick={() => applyPreset('bankTransfer')}
                                        className="px-2 py-1 text-[11px] rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold transition-colors"
                                    >
                                        Transfer (8)
                                    </button>
                                    <button
                                        onClick={selectAllColumns}
                                        className="px-2 py-1 text-[11px] rounded-md bg-primary-50 hover:bg-primary-100 text-primary-700 font-semibold transition-colors"
                                    >
                                        All ({ALL_COLUMNS.length})
                                    </button>
                                </div>
                            </div>

                            {/* Search columns */}
                            <div className="relative">
                                <Search
                                    size={13}
                                    className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="text"
                                    placeholder="Search columns..."
                                    value={columnSearch}
                                    onChange={(e) => setColumnSearch(e.target.value)}
                                    className={`${FIELD} w-full pl-7`}
                                />
                            </div>

                            {/* Column list */}
                            <div className="max-h-[350px] overflow-y-auto space-y-0.5 pr-1">
                                {filteredColumnDefs.map((col) => {
                                    const isSelected = selectedColumnKeys.includes(col.key);
                                    return (
                                        <button
                                            key={col.key}
                                            type="button"
                                            onClick={() => toggleColumn(col.key)}
                                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-left transition-colors ${
                                                isSelected
                                                    ? 'bg-primary-50 text-primary-900 font-semibold'
                                                    : 'hover:bg-gray-50 text-gray-700 font-medium'
                                            }`}
                                        >
                                            {isSelected ? (
                                                <CheckSquare size={14} className="text-primary-600 shrink-0" />
                                            ) : (
                                                <Square size={14} className="text-gray-400 shrink-0" />
                                            )}
                                            <span className="truncate">{col.label}</span>
                                        </button>
                                    );
                                })}

                                {filteredColumnDefs.length === 0 && (
                                    <p className="text-[11px] text-gray-400 text-center py-3">No columns found.</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className={showColumnPanel ? 'md:col-span-8 lg:col-span-9 min-w-0' : 'md:col-span-12 min-w-0'}>
                    {activeColumns.length === 0 ? (
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm py-20 text-center">
                            <Columns size={32} className="mx-auto text-gray-300 mb-2" />
                            <p className="text-sm font-semibold text-gray-700">No columns selected</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                                Pick columns from the panel to view data.
                            </p>
                            <button
                                onClick={() => applyPreset('essential')}
                                className="mt-4 h-9 px-4 rounded-lg bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700 transition-colors"
                            >
                                Load Essential Columns
                            </button>
                        </div>
                    ) : (
                        <Table
                            title="Bank Report Rows"
                            meta={`${filteredRows.length} of ${items.length} row${items.length === 1 ? '' : 's'} · ${activeColumns.length} column${activeColumns.length === 1 ? '' : 's'}`}
                            columns={tableColumns}
                            rows={filteredRows}
                            rowKey="id"
                            loading={loading}
                            loadingMessage="Loading details..."
                            emptyMessage={
                                items.length === 0
                                    ? 'No records found for this month.'
                                    : 'No matching records found.'
                            }
                            pageSize={10}
                            scrollClassName=""
                            emptyValue="-"
                            toolbarExtra={
                                <div className="flex flex-wrap items-end gap-3">
                                    <div>
                                        <span className={LABEL}>Month</span>
                                        <input
                                            type="month"
                                            value={selectedDate}
                                            onChange={(e) => setSelectedDate(e.target.value)}
                                            className={`${FIELD} w-[170px]`}
                                        />
                                    </div>

                                    <div>
                                        <span className={LABEL}>Grade</span>
                                        <select
                                            value={selectedGrade}
                                            onChange={(e) => setSelectedGrade(e.target.value)}
                                            className={`${FIELD} w-48`}
                                        >
                                            <option value="">All</option>
                                            {grades.map((grade) => (
                                                <option key={grade.HGD_GRADE_CODE} value={grade.HGD_GRADE_CODE}>
                                                    {grade.HGD_GRADE_NAME}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <span className={LABEL}>Search</span>
                                        <input
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Search values..."
                                            className={`${FIELD} w-[240px] max-w-full`}
                                        />
                                    </div>
                                </div>
                            }
                        />
                    )}
                </div>
            </div>
        </div>
    );
}